import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  forwardRef
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { normalizeForSearch } from 'src/app/shared/utils/search-normalize';

/**
 * Select com pesquisa (combobox), usado em todo seletor de lista (M8 /
 * CU-17tm466nzee, m8-comum.md): busca sem acento, navegação por teclado
 * (↑↓/Enter/Esc), clique fora fecha, opção selecionada marcada com ✓ e
 * rodapé "N de M <noun>". `hint` carrega a contagem de leads da lista
 * (ex. "128 leads") — quem monta `options` decide o texto.
 */
export interface SearchSelectOption {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-search-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './search-select.component.html',
  styleUrls: ['./search-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchSelectComponent),
      multi: true
    }
  ]
})
export class SearchSelectComponent implements ControlValueAccessor, OnDestroy {
  @Input() options: SearchSelectOption[] = [];
  @Input() placeholder = 'Selecione…';
  @Input() searchPlaceholder = 'Pesquisar…';
  @Input() emptyText = 'Nenhuma lista encontrada';
  /** Rótulo do rodapé "N de M <noun>", ex. 'listas', 'clientes'. */
  @Input() noun = 'listas';
  @Input() loading = false;
  @Input() testId?: string;

  @Output() selectionChange = new EventEmitter<string | null>();

  @ViewChild('searchInput') private readonly searchInputRef?: ElementRef<HTMLInputElement>;

  isOpen = false;
  search = '';
  activeIndex = 0;
  disabled = false;
  value: string | null = null;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  /**
   * Posição do dropdown em `position: fixed`, calculada a partir do botão (Bruno, 14/10): dentro de cartões
   * com `overflow: hidden` ou pouca altura a lista ficava cortada. Abre para cima quando falta espaço embaixo.
   */
  popStyle: { [key: string]: string } = {};
  listMaxHeight = 240;

  private readonly documentClickListener = (event: MouseEvent): void => this.handleDocumentClick(event);
  private readonly viewportListener = (): void => this.placePopup();

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {
    document.addEventListener('click', this.documentClickListener, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.documentClickListener, true);
    this.unbindViewport();
  }

  get selectedOption(): SearchSelectOption | undefined {
    return this.options.find(option => option.value === this.value);
  }

  get filteredOptions(): SearchSelectOption[] {
    const query = normalizeForSearch(this.search);
    if (!query) {
      return this.options;
    }
    return this.options.filter(option => normalizeForSearch(option.label).includes(query));
  }

  // --- ControlValueAccessor ---------------------------------------------

  writeValue(value: string | null): void {
    this.value = value ?? null;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // --- Interação -----------------------------------------------------------

  toggle(): void {
    if (this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    this.isOpen = true;
    this.search = '';
    this.activeIndex = 0;
    this.placePopup();
    window.addEventListener('scroll', this.viewportListener, true);
    window.addEventListener('resize', this.viewportListener);
    setTimeout(() => this.searchInputRef?.nativeElement.focus());
  }

  close(): void {
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;
    this.unbindViewport();
    this.onTouched();
  }

  /** Busca (≈54px) + rodapé (≈30px) + respiro: o resto da altura disponível vai para a lista. */
  placePopup(): void {
    const button = this.elementRef.nativeElement.querySelector('.sf-search-select__btn') as HTMLElement | null;
    if (!button) {
      return;
    }
    const rect = button.getBoundingClientRect();
    const gap = 6;
    const margin = 12;
    const chrome = 110;
    const below = window.innerHeight - rect.bottom - gap - margin;
    const above = rect.top - gap - margin;
    const openUp = below < 260 && above > below;
    const room = openUp ? above : below;
    this.listMaxHeight = Math.max(120, Math.min(320, room - chrome));
    this.popStyle = {
      position: 'fixed',
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      right: 'auto',
      ...(openUp
        ? { top: 'auto', bottom: `${window.innerHeight - rect.top + gap}px` }
        : { top: `${rect.bottom + gap}px`, bottom: 'auto' }),
    };
  }

  private unbindViewport(): void {
    window.removeEventListener('scroll', this.viewportListener, true);
    window.removeEventListener('resize', this.viewportListener);
  }

  onSearchInput(): void {
    this.activeIndex = 0;
  }

  onKeydown(event: KeyboardEvent): void {
    const opts = this.filteredOptions;
    if (event.key === 'ArrowDown') {
      this.activeIndex = Math.min(this.activeIndex + 1, Math.max(opts.length - 1, 0));
      event.preventDefault();
    } else if (event.key === 'ArrowUp') {
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
      event.preventDefault();
    } else if (event.key === 'Home') {
      this.activeIndex = 0;
      event.preventDefault();
    } else if (event.key === 'End') {
      this.activeIndex = Math.max(opts.length - 1, 0);
      event.preventDefault();
    } else if (event.key === 'Enter') {
      this.pick(opts[this.activeIndex]);
      event.preventDefault();
    } else if (event.key === 'Escape') {
      this.close();
      event.preventDefault();
    } else if (event.key === 'Tab') {
      this.close();
    }
  }

  pick(option: SearchSelectOption | undefined): void {
    if (!option || option.disabled) {
      return;
    }
    this.value = option.value;
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
    this.close();
  }

  optionId(option: SearchSelectOption): string {
    return `${this.testId || 'search-select'}-opt-${option.value}`;
  }

  /**
   * Ajuste do Bruno (14/10): quem usa `[options]` com um getter que devolve um array novo
   * a cada ciclo de detecção de mudanças (ex. `broadcast.component.ts#listOptions`) faz o
   * `*ngFor` sem `trackBy` destruir e recriar todo `<li>` a cada CD — por identidade, não
   * por valor. Rastrear por `option.value` evita esse churn de DOM.
   */
  trackByValue(_: number, option: SearchSelectOption): string {
    return option.value;
  }

  private handleDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }
}
